import { useState } from 'react';
import axios from 'axios';
import { useHandleApiErrors } from './useHandleApiErrors';
import { ApiError, ApiType, ErrorType } from 'types';
import { useAppContextSelector } from './useAppContext';
import { getAuthHeader } from 'utils';

type ClaimApplicationResponse = {
  applicationId: string;
  externalApplicationId: string;
};

type UseClaimApplication = {
  loading: boolean;
  claimApplication: (externalApplicationId: string) => Promise<ClaimApplicationResponse | undefined>;
};

type UseClaimApplicationArgs = {
  onSuccess?: (data: ClaimApplicationResponse) => void;
  onError?: (error: ApiError) => void;
};

export const useClaimApplication = ({ onSuccess, onError }: UseClaimApplicationArgs = {}): UseClaimApplication => {
  const [loading, setLoading] = useState(false);
  const { handleError } = useHandleApiErrors(ApiType.axios, onError);
  const { accessToken } = useAppContextSelector((ctx) => ctx.state);

  const claimApplication = async (externalApplicationId: string): Promise<ClaimApplicationResponse | undefined> => {
    const url = process.env.REACT_APP_APPLY_API_URL as string;
    const options = {
      headers: {
        Authorization: getAuthHeader(accessToken),
        Accept: 'application/json',
      },
    };

    try {
      setLoading(true);

      const response = await axios.post<ClaimApplicationResponse>(
        `${url}/applications/${externalApplicationId}/claim`,
        {},
        options,
      );

      setLoading(false);

      const data = response.data;
      onSuccess && onSuccess(data);
      return data;
    } catch (error) {
      setLoading(false);
      handleError({ type: ErrorType.AXIOS_NETWORK_ERROR, error });
      return undefined;
    }
  };

  return { loading, claimApplication };
};
